import { useState, useEffect, useMemo } from "react";
import { getQueryUpdateHistory } from "../services/serviceRequestService";

/**
 * Custom hook for query update history
 * @param {string} recordId - Query record ID
 * @returns {Object} Query update state
 */
export const useQueryUpdates = (recordId) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!recordId) {
      setLoading(false);
      return;
    }

    const fetchUpdates = async () => {
      setLoading(true);
      setError(null);

      const response = await getQueryUpdateHistory(recordId);

      if (response.success) {
        // Flatten all updates from different categories into a single array
        const allUpdates = [];
        Object.values(response.data || {}).forEach(updateArray => {
          if (Array.isArray(updateArray)) {
            allUpdates.push(...updateArray);
          }
        });
        setUpdates(allUpdates);
      } else {
        setError(response.error);
      }

      setLoading(false);
    };

    fetchUpdates();
  }, [recordId]);

  // Sort updates by date (newest first)
  const sortedUpdates = useMemo(() => {
    if (!updates.length) return [];

    return [...updates].sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateB - dateA; // Descending order (newest first)
    });
  }, [updates]);

  return {
    updates: sortedUpdates,
    loading,
    error,
  };
};
